import React, { useState, useEffect } from 'react'
import { Button, Card, Space, Typography, List, Tag, Popconfirm } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined, HolderOutlined } from '@ant-design/icons'
import { HStack } from 'src/react-app/ui/Stack'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { reorderAdminSteps, reorderAdminQuestions } from 'src/react-app/features/poll/api-admin'

const { Text } = Typography

interface QuizExpandedRowProps {
  quiz: any
  onEditStep: (quizId: string, step: any) => void
  onDeleteStep: (stepId: string) => void
  onAddQuestion: (quizId: string, stepId: string) => void
  onEditQuestion: (quizId: string, question: any) => void
  onDeleteQuestion: (quizId: string, questionId: string) => void
  onReorder?: () => void
}

function SortableStepCard({ step, children }: { step: any; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
  }
  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        style={{ display: 'inline-flex', cursor: 'grab', marginRight: 8, color: '#aaa' }}
      >
        <HolderOutlined />
      </div>
      {children}
    </div>
  )
}

function SortableQuestionItem({
  question,
  children,
}: {
  question: any
  children: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  }
  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', color: '#aaa', marginRight: 8, flexShrink: 0 }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

export default function QuizExpandedRow({
  quiz,
  onEditStep,
  onDeleteStep,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onReorder,
}: QuizExpandedRowProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const [steps, setSteps] = useState<any[]>(
    [...(quiz.steps || [])].sort((a, b) => a.order_index - b.order_index),
  )
  const [questionsByStep, setQuestionsByStep] = useState<Record<string, any[]>>(() => {
    const map: Record<string, any[]> = {}
    ;(quiz.questions || []).forEach((q: any) => {
      if (!q.step_id) return
      if (!map[q.step_id]) map[q.step_id] = []
      map[q.step_id].push(q)
    })
    for (const key of Object.keys(map)) {
      map[key] = [...map[key]].sort((a, b) => a.order_index - b.order_index)
    }
    return map
  })

  useEffect(() => {
    setSteps([...(quiz.steps || [])].sort((a, b) => a.order_index - b.order_index))
    const map: Record<string, any[]> = {}
    ;(quiz.questions || []).forEach((q: any) => {
      if (!q.step_id) return
      if (!map[q.step_id]) map[q.step_id] = []
      map[q.step_id].push(q)
    })
    for (const key of Object.keys(map)) {
      map[key] = [...map[key]].sort((a, b) => a.order_index - b.order_index)
    }
    setQuestionsByStep(map)
  }, [quiz])

  const handleStepDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = steps.findIndex(s => s.id === active.id)
    const newIndex = steps.findIndex(s => s.id === over.id)
    const reordered = arrayMove(steps, oldIndex, newIndex)
    setSteps(reordered)
    await reorderAdminSteps(
      quiz.id,
      reordered.map(s => s.id),
    )
    onReorder?.()
  }

  const handleQuestionDragEnd = async (stepId: string, event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const stepQuestions = questionsByStep[stepId] || []
    const oldIndex = stepQuestions.findIndex(q => q.id === active.id)
    const newIndex = stepQuestions.findIndex(q => q.id === over.id)
    const reordered = arrayMove(stepQuestions, oldIndex, newIndex)
    setQuestionsByStep(prev => ({ ...prev, [stepId]: reordered }))
    await reorderAdminQuestions(
      quiz.id,
      reordered.map(q => q.id),
    )
    onReorder?.()
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
      <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {steps.map((step: any) => (
            <SortableStepCard key={step.id} step={step}>
              <Card
                size="small"
                style={{ flex: 1 }}
                title={
                  <HStack>
                    <div>{step.title}</div>
                    <HStack gap={'16'}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => onAddQuestion(quiz.id, step.id)}
                      >
                        Добавить вопрос
                      </Button>
                      <Space>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => onEditStep(quiz.id, step)}
                          title="Редактировать шаг"
                        />
                        <Popconfirm
                          title="Удалить шаг?"
                          onConfirm={() => onDeleteStep(step.id)}
                          okText="Удалить"
                          cancelText="Отмена"
                        >
                          <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            danger
                            title="Удалить шаг"
                            disabled={(questionsByStep[step.id] || []).some(
                              (q: any) => q.config?.locked,
                            )}
                          />
                        </Popconfirm>
                      </Space>
                    </HStack>
                  </HStack>
                }
              >
                {step.description && (
                  <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                    {step.description}
                  </Text>
                )}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={e => handleQuestionDragEnd(step.id, e)}
                >
                  <SortableContext
                    items={(questionsByStep[step.id] || []).map(q => q.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <List
                      size="small"
                      dataSource={questionsByStep[step.id] || []}
                      renderItem={(question: any) => (
                        <SortableQuestionItem key={question.id} question={question}>
                          <List.Item
                            style={{ padding: '8px 0' }}
                            actions={[
                              <Button
                                key="edit"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => onEditQuestion(quiz.id, question)}
                                title="Редактировать"
                              />,
                              <Popconfirm
                                key="delete"
                                title="Удалить вопрос?"
                                onConfirm={() => onDeleteQuestion(quiz.id, question.id)}
                                okText="Удалить"
                                cancelText="Отмена"
                              >
                                <Button
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  danger
                                  title="Удалить"
                                  disabled={Boolean(question.config?.locked)}
                                />
                              </Popconfirm>,
                            ]}
                          >
                            <Space direction="vertical" size={0}>
                              <Space>
                                <Text strong>{question.title}</Text>
                                <Tag>{question.type}</Tag>
                                {question.is_required && <Tag color="red">Обязательный</Tag>}
                                {question.config?.locked && <Tag color="gold">Системный</Tag>}
                              </Space>
                              {question.subtitle && (
                                <Text type="secondary">{question.subtitle}</Text>
                              )}
                            </Space>
                          </List.Item>
                        </SortableQuestionItem>
                      )}
                    />
                  </SortableContext>
                </DndContext>
              </Card>
            </SortableStepCard>
          ))}
        </Space>
      </SortableContext>
    </DndContext>
  )
}
